/* eslint-disable no-param-reassign */
import { isEmpty } from 'lodash';
import moment, { Moment } from 'moment';
import uuid from 'react-uuid';
import { FinancialStatementFullWrite } from 'api';
import { MAX_HISTORICAL_YEARS } from 'common/config/app';
import { FIRST_ROW_TITLE, FISCAL_YEAR, FORMAT_YEAR_TITLE, LTM, NTM } from 'common/constants/financials';
import { FinancialsPerformanceMetricsColumn, FinancialStatementPeriod } from 'common/types/financials';
import { IMeasurementDate } from 'models/interfaces/measurement-date.interface';
import { END_OF_YEAR_FORMAT } from 'pages/Financials/data/dateFormatConstants';
import { getYearQuarters, MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import { ISO_DATE_FORMAT } from 'utilities/dateFormats/constants';
import { FiscalYearData } from '../performance-metrics/utilities/types';

// Utility functions
const generateParentId = (period: FinancialStatementPeriod): number | string =>
  period?.id || period?.column_ref || uuid();

const getDividerPosition = (periods: FinancialStatementPeriod[], measurementDate: string, financialsDate?: string | null): number => {
  /* Divider must be placed between the last historical year and the first projected year.
  a. If the statement date (fiscal year end) of a period is equal to the comparison date, it means
  that period has finished and the next one is a projection so we place the divider in that period.
  b. If the statement date of a period is larger than the comparison date, it means that period
  has not finished yet and it's a projection, so the divider must be placed in the period before.
  c. If financials date is provided and is before the measurement date, use the financials date
  for comparison to accurately reflect that more periods are projections. */
  
  // Use the financials date if it exists and is before the measurement date, otherwise use measurement date
  const comparisonDate = financialsDate && new Date(financialsDate) < new Date(measurementDate) 
    ? financialsDate 
    : measurementDate;
  
  let dividerPosition = 0;
  for (let index = 0; index < periods.length; index++) {
    // Break out of the loop when the first projection year is found
    if (new Date(periods[index].statement_date as string) >= new Date(comparisonDate)) {
      dividerPosition = index;
      break;
    }
  }

  if (periods[dividerPosition]?.isProjectedYear) {
    dividerPosition -= 1;
  }

  return dividerPosition;
};

const loadFinancialStatementData = (
  selectedMeasurementDate: IMeasurementDate,
  financialStatement: FinancialStatementFullWrite,
  fiscalYearData: FiscalYearData,
  setPeriods: (periods: FinancialStatementPeriod[]) => void,
  historicalYearsProps: {
    setAdditionalHistoricalYears: (additionalHistoricalYears: FinancialStatementPeriod[]) => void;
    showAdditionalHistoricalYears: boolean;
  },
  currentFiscalYearEnd: Moment
) => {
  const { setAdditionalHistoricalYears, showAdditionalHistoricalYears } = historicalYearsProps;

  if (!selectedMeasurementDate) {
    return;
  }

  const mdDate = moment(selectedMeasurementDate.date, ISO_DATE_FORMAT);
  const monthDayMD = `${mdDate.month() + 1}-${mdDate.date()}`;
  const { end_month, end_day } = fiscalYearData.fiscal_year_end ?? {};
  const monthDayFY = `${end_month}-${end_day}`;

  const measurementDate = selectedMeasurementDate?.date;
  const prevPeriods = [];
  const nextPeriods = [];
  const generatedPeriods: FinancialStatementPeriod[] = [];

  const auxPeriods: FinancialStatementPeriod[] = financialStatement.financial_statement_periods || [];
  const fiscalYearPeriods = auxPeriods
    .filter(p => p.period_type === FISCAL_YEAR)
    .sort((a, b) => moment(a.statement_date).diff(moment(b.statement_date)));
  const statementYears = fiscalYearPeriods.map(p => parseInt(p.statement_date ?? '', 10)).toSorted((a, b) => a - b);
  
  // Use financials date if it exists and is before the measurement date, otherwise use measurement date
  // This determines which fiscal years are historical vs projected
  // When financials date is earlier than measurement date (e.g., financials as of 07/31/2025 but MD is 12/31/2025),
  // fiscal years ending after the financials date should be marked as projections
  const effectiveDate = financialStatement.financials_date && 
    new Date(financialStatement.financials_date) < new Date(measurementDate)
    ? financialStatement.financials_date 
    : measurementDate;
  const currentPeriod = moment(effectiveDate);

  const currentFiscalYearEndDate = currentFiscalYearEnd.format(ISO_DATE_FORMAT);
  let tmpAdditionalHistoricalYearsColumns: FinancialStatementPeriod[] = [];

  const generateParentColumns = ({ id, period, ...info }: FinancialsPerformanceMetricsColumn) => {
    // Extract freshly calculated title/subtitle before spreading period
    const { title: infoTitle, subtitle: infoSubtitle } = info;
    
    // For LTM/NTM, subtitle comes from statement_date
    const finalSubtitle = (info.isLTM || info.isNTM) && period?.statement_date 
      ? period.statement_date 
      : infoSubtitle;

    generatedPeriods.push({
      ...info,
      ...period,
      // Explicitly preserve freshly calculated values after period spread
      title: infoTitle,
      subtitle: finalSubtitle,
      isParent: true,
      name: info?.title || period?.name,
      id,
    } as FinancialStatementPeriod);
  };

  // Once backend removes quarters entirely, remove the dependency on years and compute annual-only dates directly.
  const lastAvailableStatementYear = statementYears[statementYears.length - 1];

  const currentPeriodYear = currentPeriod.year();

  // Helper variables to determine the limits of the historical years that we will display
  const defaultLimitYear = currentPeriodYear - MAX_HISTORICAL_YEARS;
  const historicalYears = statementYears.filter(year => year < currentPeriodYear);
  tmpAdditionalHistoricalYearsColumns = historicalYears
    .filter(year => year <= defaultLimitYear)
    .map(year => ({ id: null, year }) as unknown as FinancialStatementPeriod);

  // If the financial statement has not been saved yet
  // we will display the previous two years
  if (isEmpty(fiscalYearPeriods)) {
    prevPeriods.push(moment(currentFiscalYearEndDate).subtract(2, 'years'));
    prevPeriods.push(moment(currentFiscalYearEndDate).subtract(1, 'years'));
  } else {
    /* Otherwise, create columns for the previous years that are in the financial statement
    but are consecutive */

    // check to see if there are enough financial periods to get to the currentFiscalYearEnd
    const lastFiscalYear = moment(fiscalYearPeriods[fiscalYearPeriods.length - 1].statement_date);
    // get the number of years between the last fiscal year and the current fiscal year
    const yearsBetween = currentFiscalYearEnd.year() - lastFiscalYear.year();
    // for every yearsBetween, check if it should be historical or projected based on effectiveDate
    for (let i = 1; i < yearsBetween; i++) {
      const fiscalYearDate = moment(lastFiscalYear).add(i, 'years');
      // If this fiscal year ends after the effective date, it's a projection
      if (fiscalYearDate.isAfter(currentPeriod) || fiscalYearDate.isSame(currentPeriod, 'day')) {
        nextPeriods.push(fiscalYearDate);
      } else {
        prevPeriods.push(fiscalYearDate);
      }
    }

    const limitAllHistoricalYears = currentPeriodYear - (historicalYears.length + 1);
    // Include current fiscal year in range check so it can be reclassified based on effective date
    const isInAcceptableRange = (year: number) => year <= currentFiscalYearEnd.year() && year > limitAllHistoricalYears;

    statementYears.forEach(year => {
      const rangeMatch = isInAcceptableRange(year);
      if (rangeMatch) {
        const fiscalYearEndDate = moment(currentFiscalYearEndDate).subtract(currentFiscalYearEnd.year() - year, 'years');
        // Check if this fiscal year ends after the effective date (financials date)
        // If so, it should be a projection, not historical
        if (fiscalYearEndDate.isAfter(currentPeriod)) {
          nextPeriods.push(fiscalYearEndDate);
        } else {
          prevPeriods.push(fiscalYearEndDate);
        }
      }
    });
  }

  // Only add current fiscal year if it wasn't already added from statementYears
  const currentFiscalYearAlreadyAdded = 
    prevPeriods.some(p => p.year() === currentFiscalYearEnd.year()) ||
    nextPeriods.some(p => p.year() === currentFiscalYearEnd.year());

  if (!currentFiscalYearAlreadyAdded) {
    const currentPeriodBeforeFiscalYearEnd = currentPeriod.isBefore(currentFiscalYearEnd);
    // Add current fiscal year to prevPeriods if the effective date is on or after the fiscal year end
    if (!currentPeriodBeforeFiscalYearEnd) {
      prevPeriods.push(moment(currentFiscalYearEndDate));
    } else {
      // Add current fiscal year to nextPeriods if the effective date is before the fiscal year end
      nextPeriods.push(moment(currentFiscalYearEndDate));
    }
  }

  // if none of the available statementYears comply with continuity, add years that do.
  // we know that it will be the previous two years because at this point the currentPeriod will have had to been placed
  if (prevPeriods.length === 0) {
    prevPeriods.push(moment(currentFiscalYearEndDate).subtract(2, 'years'));
    prevPeriods.push(moment(currentFiscalYearEndDate).subtract(1, 'years'));
  }
  /*
  Display the following 3 years by default (for new companies) or if the last available statement year is
  far from the currentPeriodDate */
  if (isEmpty(fiscalYearPeriods) || lastAvailableStatementYear < currentPeriodYear) {
    nextPeriods.push(moment(currentFiscalYearEndDate).add(1, 'years'));
    if (!financialStatement.id) {
      nextPeriods.push(moment(currentFiscalYearEndDate).add(2, 'years'));
      nextPeriods.push(moment(currentFiscalYearEndDate).add(3, 'years'));
    }
  } else {
    // Otherwise, create columns for the next years that are in the financial statement
    statementYears.forEach(year => {
      if (year > currentFiscalYearEnd.year()) {
        nextPeriods.push(moment(currentFiscalYearEndDate).add(year - currentFiscalYearEnd.year(), 'years'));
      }
    });
    // if none of the available statementYears comply with continuity, add the next year
    if (nextPeriods.length === 0) {
      nextPeriods.push(moment(currentFiscalYearEndDate).add(1, 'years'));
    }
  }
  // Organize the periods
  prevPeriods.sort((a, b) => a.diff(b));
  nextPeriods.sort((a, b) => a.diff(b));

  const finalAutomaticPeriod = nextPeriods[nextPeriods.length - 1];
  fiscalYearPeriods
    .map(p => moment(p.statement_date))
    .filter(p => p.isAfter(finalAutomaticPeriod))
    .sort((a, b) => a.unix() - b.unix())
    .forEach(p => {
      nextPeriods.push(p);
    });

  // years cannot be repeated as they're the data source of the parent columns but
  // in some FYE and MD combinations, nextPeriods may have different day and month but with equal year
  // so we deal with it here
  const uniqueNextPeriods = nextPeriods.reduce(
    (accumulator, momentObj) => {
      const periodYear = momentObj.year();

      if (!accumulator.years.has(periodYear)) {
        accumulator.years.add(periodYear);
        accumulator.result.push(momentObj);
      }

      return accumulator;
    },
    { years: new Set(), result: [] as Moment[] }
  ).result;

  prevPeriods.forEach((date, indexDate) => {
    const year = date.year();
    const isAdditionalHistoricalYear = year <= defaultLimitYear;
    const periodYear =
      fiscalYearPeriods.find(period => date.isSame(period.statement_date, 'year')) || ({} as FinancialStatementPeriod);
    const parentId = generateParentId(periodYear);
    const quarters = getYearQuarters({
      yearKey: year,
      fiscalYearData,
    });

    if (isAdditionalHistoricalYear) {
      tmpAdditionalHistoricalYearsColumns.forEach(column => {
        // any due to conflicting types
        if ((column as any).year === year) {
          (column as any).id = parentId as any;
        }
        return column;
      });
    }

    const q4 = moment(quarters[quarters.length - 1].date, MOMENT_DEFAULT_DATE_FORMAT, false);

    // name and date should refer to last quarter of q4
    const isVisibleColumn = isAdditionalHistoricalYear
      ? isAdditionalHistoricalYear && showAdditionalHistoricalYears
      : null;
    generateParentColumns({
      id: parentId as any,
      statement_date: q4.format(ISO_DATE_FORMAT),
      title: FORMAT_YEAR_TITLE(q4.format('YYYY')),
      period_type: FISCAL_YEAR,
      name: q4.format('YYYY'),
      isPrevYear: true,
      period: periodYear,
      isLTM: false,
      isNTM: false,
      balanceSheetMatchesFiscalYearEnd: monthDayFY === monthDayMD,
      mdMatchesFiscalYearEnd: monthDayFY === monthDayMD,
      isParentWithDivider: false,
      year: q4.format('YYYY'),
      isAdditionalHistoricalYear,
      showAdditionalHistoricalYear: showAdditionalHistoricalYears,
      isVisibleColumn: Boolean(isVisibleColumn),
    });
  });

  uniqueNextPeriods.forEach((date, indexDate) => {
    const periodYear = fiscalYearPeriods.find(p => date.isSame(p.statement_date, 'year'));
    const parentId = generateParentId(periodYear!);
    const quarters = getYearQuarters({
      yearKey: date.year(),
      fiscalYearData,
    });

    const q4 = moment(quarters[quarters.length - 1].date, MOMENT_DEFAULT_DATE_FORMAT, false);

    generateParentColumns({
      id: parentId as number,
      statement_date: q4.format(ISO_DATE_FORMAT),
      title: FIRST_ROW_TITLE,
      subtitle: FORMAT_YEAR_TITLE(q4.format('YYYY')),
      period_type: FISCAL_YEAR,
      is_projection: true,
      name: q4.format('YYYY'),
      isProjectedYear: true,
      mdMatchesFiscalYearEnd: monthDayFY === monthDayMD,
      isFirstProjectedYear: indexDate === 0,
      isParentWithDivider: false,
      period: periodYear,
      isLTM: false,
      isNTM: false,
      year: q4.format('YYYY'),
    });
  });

  // LTM & NTM
  const periodLTM = auxPeriods.find(period => period.period_type === LTM)!;

  const ltmParentId = generateParentId(periodLTM);
  // LTM
  const getReportedStatementDate = (period: FinancialStatementPeriod) => {
    const date = period?.income_statement?.reported_statement_date ?? period.statement_date;
    return moment(date).format(END_OF_YEAR_FORMAT);
  };
  const getBalanceSheetReportedStatementDate = (period: FinancialStatementPeriod) => {
    const date = period.balance_sheet?.reported_statement_date ?? period.statement_date;
    return moment(date).format(END_OF_YEAR_FORMAT);
  };

  const ltmStatementMonthDay = getReportedStatementDate(periodLTM);
  const balanceSheetLtmStatementMonthDay = getBalanceSheetReportedStatementDate(periodLTM);
  generateParentColumns({
    id: ltmParentId as number,
    statement_date: measurementDate,
    title: 'LTM',
    period_type: LTM,
    name: 'LTM',
    subtitle: measurementDate,
    balanceSheetMatchesFiscalYearEnd: monthDayFY === balanceSheetLtmStatementMonthDay,
    mdMatchesFiscalYearEnd: monthDayFY === ltmStatementMonthDay,
    period: periodLTM,
    isParentWithDivider: false,
    isLTM: true,
    isNTM: false,
    isLTMYear: true,
    year: String(currentFiscalYearEnd.year()),
  });

  const periodNTM = auxPeriods.find(period => period.period_type === NTM) || ({} as FinancialStatementPeriod);
  const ntmParentId = generateParentId(periodNTM);

  const yearAfterMeasurementDate = moment(measurementDate).add(1, 'years');

  const ntmStatementMonthDay = getReportedStatementDate(periodNTM);
  generateParentColumns({
    id: ntmParentId as number,
    statement_date: yearAfterMeasurementDate.format(ISO_DATE_FORMAT),
    title: 'NTM',
    period_type: NTM,
    name: 'NTM',
    subtitle: yearAfterMeasurementDate.format(ISO_DATE_FORMAT),
    period: periodNTM,
    mdMatchesFiscalYearEnd: monthDayFY === ntmStatementMonthDay,
    isParentWithDivider: false,
    isNTM: true,
    isLTM: false,
    isNTMYear: true,
    year: String(currentFiscalYearEnd.year() + 1),
  });
  const dividerPosition = getDividerPosition(generatedPeriods, measurementDate, financialStatement.financials_date);
  generatedPeriods[dividerPosition].isParentWithDivider = true;
  
  // Set divider for the LTM column (visual separation between the last projection period and the LTM column)
  const ltmIndex = generatedPeriods.findIndex(period => period.isLTM);
  if (ltmIndex >= 0) {
    generatedPeriods[ltmIndex].needsSpacer = true;
  }
  
  setPeriods(generatedPeriods);
  tmpAdditionalHistoricalYearsColumns.reverse();
  setAdditionalHistoricalYears(tmpAdditionalHistoricalYearsColumns as unknown as FinancialStatementPeriod[]);
};

export default loadFinancialStatementData;
